// Shared Account / PlanYear document library.
//
// Reusable across:
// - AccountDetailPage (host_kind=account, evergreen folders)
// - PlanYear detail page (host_kind=plan_year, renewal-cycle
//   folders)
//
// Owns: folder navigation with breadcrumbs, drag/drop + button
// upload via the presign + finalize round-trip, file preview that
// opens a presigned URL, soft-delete of nodes, and an empty state
// for folders with no children.
//
// File preview rules (mirroring the v1 viewer):
// - PDF → render in an inline iframe.
// - image/* → inline <img>.
// - text/plain (small) → inline <pre>.
// - everything else → metadata + open-in-new-tab download button.

import {
  ChevronRight, Download, File as FileIcon,
  FilePlus, Folder, FolderPlus, Trash2,
  X,
} from "lucide-react";
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import {
  createDocumentFolder, deleteDocumentNode, listDocuments,
  previewDocumentNode, uploadFileViaPresign,
  type DocumentHost,
  type DocumentLibraryListing,
  type DocumentNodeFile,
  type DocumentPreviewResponse,
} from "./api";


export interface DocumentLibraryProps {
  host: DocumentHost;
  /** Optional title; defaults to "Documents". */
  title?: string;
  /** When false, hide create/upload affordances (e.g. archived
   *  PlanYears can still browse history but not write). */
  readOnly?: boolean;
  /** Initial folder to open. The library re-syncs to this value
   *  whenever it changes (e.g. parent reads ``?folder=`` from
   *  the URL and re-passes), but the user's own folder navigation
   *  inside the library stays intact between updates. */
  initialFolder?: string;
}


export function DocumentLibrary({
  host, title = "Documents", readOnly, initialFolder,
}: DocumentLibraryProps) {
  const [folder, setFolder] = useState<string>(initialFolder ?? "/");
  // Re-sync when the parent passes a new initialFolder (e.g.
  // navigating from PlanYear detail's "Open plan-year folder"
  // shortcut, which writes ``?folder=/plan-years/2026-2027/``).
  useEffect(() => {
    if (initialFolder !== undefined) setFolder(initialFolder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFolder]);
  const [listing, setListing] = useState<DocumentLibraryListing | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [preview, setPreview] = useState<DocumentPreviewResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listDocuments(host, folder);
      setListing(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't load.");
    } finally {
      setLoading(false);
    }
  }, [host, folder]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(folder),
    [folder],
  );

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    setUploadStatus(`Uploading ${arr.length} file${arr.length === 1 ? "" : "s"}…`);
    try {
      for (const f of arr) {
        await uploadFileViaPresign(host, f, folder);
      }
      setUploadStatus("Uploaded.");
      await refresh();
    } catch (err: unknown) {
      setUploadStatus(
        err instanceof Error ? `Upload failed: ${err.message}` : "Upload failed.",
      );
    }
  }

  async function handleNewFolder() {
    const raw = window.prompt("New folder name (letters, digits, dashes):");
    if (!raw) return;
    const safe = raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    const next = `${folder}${safe}/`;
    try {
      await createDocumentFolder(host, next);
      await refresh();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Couldn't create folder.",
      );
    }
  }

  async function handleDelete(file: DocumentNodeFile) {
    if (!window.confirm(`Delete ${file.filename}?`)) return;
    try {
      await deleteDocumentNode(host, file.node_id);
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't delete.");
    }
  }

  async function handlePreview(file: DocumentNodeFile) {
    try {
      const next = await previewDocumentNode(host, file.node_id);
      setPreview(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Couldn't preview.");
    }
  }

  async function handleDownload(file: DocumentNodeFile) {
    // Reuse the preview presign — Spaces returns the same signed URL
    // for inline preview and download. Open it in a new tab so the
    // browser handles save / inline display per its own rules; the
    // user can then Save As from there.
    try {
      const next = await previewDocumentNode(host, file.node_id);
      window.open(next.url, "_blank", "noopener,noreferrer");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Couldn't download.",
      );
    }
  }

  return (
    <section
      data-testid="document-library"
      data-host-kind={host.hostKind}
      onDragOver={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragActive(false);
      }}
      onDrop={(e) => {
        if (readOnly) return;
        e.preventDefault();
        setDragActive(false);
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          void handleFiles(e.dataTransfer.files);
        }
      }}
      className={[
        "flex flex-col gap-3 rounded-md border bg-canvas/40 p-4",
        dragActive
          ? "border-teal ring-1 ring-teal"
          : "border-border-subtle",
      ].join(" ")}
    >
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="document-library-new-folder"
              onClick={() => void handleNewFolder()}
              className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-1 text-xs text-text-secondary hover:text-text-primary"
            >
              <FolderPlus size={12} aria-hidden="true" />
              New folder
            </button>
            <button
              type="button"
              data-testid="document-library-upload"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-md bg-teal px-2 py-1 text-xs font-medium text-text-inverse hover:opacity-90"
            >
              <FilePlus size={12} aria-hidden="true" />
              Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              data-testid="document-library-file-input"
              className="sr-only"
              onChange={(e) => {
                if (e.target.files) void handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
        )}
      </header>

      <Breadcrumbs crumbs={breadcrumbs} onNavigate={setFolder} />

      {loading ? (
        <p className="text-xs italic text-text-tertiary">Loading…</p>
      ) : error ? (
        <p
          data-testid="document-library-error"
          role="alert"
          className="rounded-md bg-ember-tint px-3 py-2 text-sm text-ember-ink"
        >
          Couldn't load files. {error}
        </p>
      ) : listing && (
        <DocumentListing
          listing={listing}
          onOpenFolder={setFolder}
          onPreview={handlePreview}
          onDelete={handleDelete}
          onDownload={handleDownload}
          readOnly={readOnly}
        />
      )}

      {uploadStatus && (
        <p
          data-testid="document-library-upload-status"
          className="text-xs text-text-tertiary"
        >
          {uploadStatus}
        </p>
      )}

      {preview && (
        <PreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
        />
      )}
    </section>
  );
}


/** Display-only collapse for plan-year folder names: a folder named
 * ``YYYY-YYYY`` whose start year equals its end year reads naturally
 * as just ``YYYY``. Path / lookup keys still use the full segment so
 * existing files don't break. */
export function prettifyFolderName(name: string): string {
  const match = /^(\d{4})-(\d{4})$/.exec(name);
  if (match && match[1] === match[2]) return match[1];
  return name;
}


function buildBreadcrumbs(folder: string): Array<{ label: string; path: string }> {
  if (folder === "/" || folder === "") {
    return [{ label: "Root", path: "/" }];
  }
  const parts = folder.split("/").filter(Boolean);
  const out: Array<{ label: string; path: string }> = [
    { label: "Root", path: "/" },
  ];
  let acc = "/";
  for (const seg of parts) {
    acc += `${seg}/`;
    out.push({ label: prettifyFolderName(seg), path: acc });
  }
  return out;
}


function Breadcrumbs({
  crumbs, onNavigate,
}: {
  crumbs: Array<{ label: string; path: string }>;
  onNavigate: (path: string) => void;
}) {
  return (
    <nav
      data-testid="document-library-breadcrumbs"
      className="flex flex-wrap items-center gap-1 text-xs text-text-secondary"
    >
      {crumbs.map((c, idx) => (
        <span key={c.path} className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`document-library-crumb-${idx}`}
            onClick={() => onNavigate(c.path)}
            className="hover:text-text-primary hover:underline"
          >
            {c.label}
          </button>
          {idx < crumbs.length - 1 && (
            <ChevronRight size={11} aria-hidden="true" />
          )}
        </span>
      ))}
    </nav>
  );
}


function DocumentListing({
  listing, onOpenFolder, onPreview, onDelete, onDownload, readOnly,
}: {
  listing: DocumentLibraryListing;
  onOpenFolder: (path: string) => void;
  onPreview: (file: DocumentNodeFile) => void;
  onDelete: (file: DocumentNodeFile) => void;
  onDownload: (file: DocumentNodeFile) => void;
  readOnly?: boolean;
}) {
  const empty =
    listing.folders.length === 0 && listing.files.length === 0;
  if (empty) {
    return (
      <p
        data-testid="document-library-empty"
        className="text-xs italic text-text-tertiary"
      >
        No files in this folder yet.
        {!readOnly && " Drop files here or use Upload to add."}
      </p>
    );
  }
  return (
    <ul
      role="list"
      data-testid="document-library-list"
      className="flex flex-col divide-y divide-border-subtle"
    >
      {listing.folders.map((f) => (
        <li
          key={f.path}
          data-testid={`document-library-folder-${f.name}`}
          className="flex items-center gap-2 px-1 py-2"
        >
          <Folder size={14} className="text-teal" aria-hidden="true" />
          <button
            type="button"
            onClick={() => onOpenFolder(f.path)}
            className="flex-1 text-left text-sm text-text-primary hover:underline"
          >
            {prettifyFolderName(f.name)}
          </button>
        </li>
      ))}
      {listing.files.map((f) => (
        <li
          key={f.node_id}
          data-testid={`document-library-file-${f.node_id}`}
          className="flex items-center gap-2 px-1 py-2"
        >
          <FileIcon size={14} className="text-text-tertiary" aria-hidden="true" />
          <button
            type="button"
            data-testid={`document-library-preview-${f.node_id}`}
            onClick={() => onPreview(f)}
            className="flex-1 text-left text-sm text-text-primary hover:underline"
          >
            {f.filename}
          </button>
          <span className="text-[11px] text-text-tertiary">
            {formatSize(f.size_bytes)}
          </span>
          <button
            type="button"
            data-testid={`document-library-download-${f.node_id}`}
            aria-label={`Download ${f.filename}`}
            title="Download"
            onClick={() => onDownload(f)}
            className="text-text-tertiary hover:text-teal"
          >
            <Download size={12} aria-hidden="true" />
          </button>
          {!readOnly && (
            <button
              type="button"
              data-testid={`document-library-delete-${f.node_id}`}
              aria-label={`Delete ${f.filename}`}
              title="Delete"
              onClick={() => onDelete(f)}
              className="group text-text-tertiary"
            >
              <Trash2
                size={12}
                aria-hidden="true"
                className="group-hover:stroke-ember"
              />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}


function PreviewModal({
  preview, onClose,
}: {
  preview: DocumentPreviewResponse;
  onClose: () => void;
}) {
  const kind = previewKind(preview.content_type);
  return (
    <div
      data-testid="document-library-preview"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-canvas/80"
    >
      <div className="relative flex max-h-[90vh] max-w-3xl flex-col gap-3 rounded-md border border-border-subtle bg-raised p-4 shadow-lg">
        <header className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-text-primary">
            {preview.filename}
          </h4>
          <button
            type="button"
            data-testid="document-library-preview-close"
            aria-label="Close preview"
            onClick={onClose}
            className="text-text-tertiary hover:text-text-primary"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </header>
        <div className="min-w-[40vw]">
          {kind === "pdf" && (
            <iframe
              data-testid="document-library-preview-iframe"
              src={preview.url}
              title={preview.filename}
              className="h-[70vh] w-full rounded-md border border-border-subtle"
            />
          )}
          {kind === "image" && (
            <img
              data-testid="document-library-preview-image"
              src={preview.url}
              alt={preview.filename}
              className="max-h-[70vh] max-w-full"
            />
          )}
          {kind === "other" && (
            <div className="flex flex-col gap-2 text-sm text-text-secondary">
              <p>
                Preview not supported for{" "}
                <code>{preview.content_type}</code>.
              </p>
              <a
                data-testid="document-library-preview-download"
                href={preview.url}
                target="_blank" rel="noreferrer"
                className="inline-flex w-fit items-center gap-1 rounded-md bg-teal px-2 py-1 text-xs font-medium text-text-inverse hover:opacity-90"
              >
                <Download size={12} aria-hidden="true" />
                Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function previewKind(contentType: string): "pdf" | "image" | "other" {
  if (contentType.startsWith("application/pdf")) return "pdf";
  if (contentType.startsWith("image/")) return "image";
  return "other";
}


function formatSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
